import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash, Check, Lightning, Receipt, CalendarBlank } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;
const nextRecurringDate = (dueDate, frequency) => {
  if (!dueDate) return '';

  const date = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';

  if (frequency === 'quarterly') {
    date.setMonth(date.getMonth() + 3);
  } else if (frequency === 'yearly') {
    date.setFullYear(date.getFullYear() + 1);
  } else {
    date.setMonth(date.getMonth() + 1);
  }

  return date.toISOString().slice(0, 10);
};

const CATEGORIES = ['utility', 'subscription', 'bill', 'insurance', 'rent', 'other'];
const CAT_ICONS = { utility: Lightning, subscription: Receipt, bill: Receipt, insurance: Receipt, rent: CalendarBlank, other: Receipt };

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [payDialog, setPayDialog] = useState(null);
  const [payBankId, setPayBankId] = useState('');
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({ name: '', category: 'utility', amount: '', due_date: '', is_recurring: false, frequency: 'monthly' });

  const load = () => {
    Promise.all([
      api.get('/payments').then((r) => setPayments(r.data)),
      api.get('/banks').then((r) => setBanks(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payments', { ...form, amount: parseFloat(form.amount) });
      setDialogOpen(false);
      setForm({ name: '', category: 'utility', amount: '', due_date: '', is_recurring: false, frequency: 'monthly' });
      load();
    } catch {}
  };

  const handlePay = async () => {
    if (!payDialog) return;
    try {
      await api.put(`/payments/${payDialog.id}/pay`, { bank_id: payBankId || null });
      setPayDialog(null);
      setPayBankId('');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Payment failed');
    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/payments/${id}`);
    load();
  };

  const pending = payments.filter((p) => p.status === 'pending');
  const paid = payments.filter((p) => p.status === 'paid');
  const totalPending = pending.reduce((s, p) => s + p.amount, 0);
  const recurringPreviewDate = nextRecurringDate(form.due_date, form.frequency);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Payments</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Manage bills, utilities, and subscriptions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-payment-btn" className="bg-[#1E3F2D] text-white font-semibold rounded-xl px-5 py-2.5 hover:bg-[#29563E] transition-colors active:scale-95 flex items-center gap-2 text-sm">
              <Plus size={18} weight="bold" /> Add Payment
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E8E6E1] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>New Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <label htmlFor="payment-name" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Name</label>
                <input
                  id="payment-name"
                  name="name"
                  data-testid="payment-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                  placeholder="Electricity Bill"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="payment-category" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Category</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger id="payment-category" name="category" className="w-full border-[#E8E6E1] rounded-lg" data-testid="payment-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="payment-amount" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Amount</label>
                  <input
                    id="payment-amount"
                    name="amount"
                    data-testid="payment-amount-input"
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="payment-due-date" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Due Date</label>
                <input
                  id="payment-due-date"
                  name="due_date"
                  data-testid="payment-due-date-input"
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                  required
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={form.is_recurring}
                  onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                  className="w-4 h-4 rounded border-[#E8E6E1] text-[#1E3F2D] focus:ring-[#1E3F2D]"
                  data-testid="recurring-checkbox"
                />
                <label htmlFor="recurring" className="text-sm text-[#5C5A56]">Recurring payment</label>
                {form.is_recurring && (
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger id="payment-frequency" name="frequency" className="w-32 border-[#E8E6E1] rounded-lg h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              {form.is_recurring && form.due_date && (
                <div className="rounded-xl border border-[#E8E6E1] bg-[#FBF9F6] px-4 py-3 text-sm">
                  <p className="text-[#1C1B1A] font-semibold">Recurring payment preview</p>
                  <p className="text-[#5C5A56] mt-1">First due date: <span className="font-semibold">{form.due_date}</span></p>
                  <p className="text-[#5C5A56] mt-1">Next due date: <span className="font-semibold">{recurringPreviewDate}</span></p>
                  <p className="text-[#5C5A56] mt-1">The amount starts at <span className="font-semibold">{fmt(form.amount)}</span> and can be changed later if needed.</p>
                </div>
              )}
              <button data-testid="submit-payment-btn" type="submit" className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-95">
                Add Payment
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={!!payDialog} onOpenChange={() => setPayDialog(null)}>
        <DialogContent className="bg-white border-[#E8E6E1] rounded-2xl">
          <DialogHeader><DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Pay Bill: {payDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-[#FBF9F6] rounded-xl p-3 text-center">
              <p className="text-xs text-[#5C5A56]">Amount to Pay</p>
              <p className="text-2xl font-bold font-mono-numbers text-[#A63C24]">{fmt(payDialog?.amount)}</p>
            </div>
            <div>
              <label htmlFor="pay-bank" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Pay From Account</label>
              <Select value={payBankId || 'none'} onValueChange={(value) => setPayBankId(value === 'none' ? '' : value)}>
                <SelectTrigger id="pay-bank" name="bank_id" className="w-full border-[#E8E6E1] rounded-lg" data-testid="pay-bank-select"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {banks.map((b) => (
                    <SelectItem key={b.id} value={b.id} disabled={b.balance < (payDialog?.amount || 0)}>
                      {b.bank_name} - {fmt(b.balance)} {b.balance < (payDialog?.amount || 0) ? '(Insufficient)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {payBankId && banks.find((b) => b.id === payBankId) && (
                <p className="text-xs text-[#5C5A56] mt-1">
                  Available: <span className="font-mono-numbers font-semibold">{fmt(banks.find((b) => b.id === payBankId)?.balance)}</span>
                  {banks.find((b) => b.id === payBankId)?.balance < (payDialog?.amount || 0) && (
                    <span className="text-[#A63C24] ml-2 font-semibold">Insufficient balance!</span>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={handlePay}
              data-testid="confirm-pay-btn"
              className="w-full bg-[#265C42] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#1E3F2D] transition-colors active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirm Payment
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Pending Bills</p>
          <p className="text-2xl font-bold font-mono-numbers text-[#A63C24]" data-testid="total-pending">{fmt(totalPending)}</p>
          <p className="text-xs text-[#5C5A56] mt-1">{pending.length} payments due</p>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Paid This Month</p>
          <p className="text-2xl font-bold font-mono-numbers text-[#265C42]">{paid.length}</p>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Total Payments</p>
          <p className="text-2xl font-bold font-mono-numbers text-[#1C1B1A]">{payments.length}</p>
        </div>
      </div>

      {pending.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-3">Pending</h3>
          <div className="space-y-3" data-testid="pending-payments">
            {pending.map((p) => {
              const CatIcon = CAT_ICONS[p.category] || Receipt;
              return (
                <div key={p.id} className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 flex items-center justify-between hover:shadow-md transition-all" data-testid={`payment-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FBF9F6] rounded-xl flex items-center justify-center">
                      <CatIcon size={18} weight="duotone" className="text-[#D98324]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1C1B1A]">{p.name}</p>
                      <p className="text-xs text-[#5C5A56]">
                        Due: {p.due_date}
                        {p.is_recurring && ` (${p.frequency})`}
                        {p.next_payment_date && ` | Next: ${p.next_payment_date}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono-numbers text-sm font-semibold text-[#A63C24]">{fmt(p.amount)}</span>
                    <button onClick={() => { setPayDialog(p); setPayBankId(''); }} className="bg-[#265C42] text-white p-2 rounded-lg hover:bg-[#1E3F2D] transition-colors" data-testid={`pay-${p.id}`} title="Pay this bill">
                      <Check size={14} weight="bold" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="text-[#5C5A56] hover:text-[#A63C24] transition-colors p-1">
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {paid.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-3">Completed</h3>
          <div className="space-y-3" data-testid="paid-payments">
            {paid.map((p) => (
              <div key={p.id} className="bg-white/60 border border-[#E8E6E1] rounded-2xl p-4 flex items-center justify-between opacity-70">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#265C42]/10 rounded-xl flex items-center justify-center">
                    <Check size={18} weight="bold" className="text-[#265C42]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1C1B1A] line-through">{p.name}</p>
                    <p className="text-xs text-[#5C5A56]">
                      {p.category}
                      {p.is_recurring && p.next_payment_date ? ` | Next: ${p.next_payment_date}` : ''}
                    </p>
                  </div>
                </div>
                <span className="font-mono-numbers text-sm text-[#5C5A56]">{fmt(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {payments.length === 0 && !loading && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 text-center text-[#5C5A56] text-sm">
          No payments added yet. Track your utility bills and subscriptions!
        </div>
      )}
    </div>
  );
}
